import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Update } from '@ngrx/entity';
import { select, Store } from '@ngrx/store';
import { combineLatest, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { Property, TrainingStatus } from 'src/app/models';
import { SettingsService } from '../../settings/services/settings.service';
import { currentOrganization } from '../auth';
import * as AuthActions from '../auth/auth.actions';
import { AppState } from '../core.state';
import * as PropertyActions from './properties.actions';
import { selectCurrentPropertyId, selectPropertyById } from './properties.selectors';
import * as ToastrActions from './toastr.actions';
import * as KbActions from '../../kb/store/kb.actions';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CharlaScrapedPagePreviewComponent } from 'src/app/settings/scraped-page-preview/scraped page-preview.component';
import { FacebookPageSelectorComponent } from 'src/app/settings/facebookpage-selector/facebookpage-selector.component';
import { I18nService } from '../i18n';


@Injectable()
export class PropertiesEffects {
    private i18n = inject(I18nService);

    saveProperty = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.PropertySave),
        mergeMap(action => combineLatest( [
                of(action),
                this.store.pipe( select(currentOrganization), filter(o => o != null), take(1) )
            ])
        ),
        mergeMap( ([action, org]) => 
            this.settingsService.saveProperty(action.property, org.id).pipe(
                switchMap(res => [
                    PropertyActions.PropertySaveSuccess({property: res.model}),
                    ToastrActions.showToastrSuccess({message: this.i18n.t('core.properties.property_was_successfully_saved', 'Property was successfully saved.'), title: ''}),
                    // Refresh the user properties after we added a new one
                    AuthActions.UserRequested(),
                    // Refresh the isKbWidget flag in case the save is initiated from the quick wizard
                    KbActions.KbRequest()
                ]),
                catchError((error: HttpErrorResponse) => of( 
                    PropertyActions.PropertySaveFailed({message: error.error.detail}),
                    ToastrActions.showToastrError({message: error.error.detail , title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
                ))
            )
        )
    ));

    deleteProperty = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.PropertyDeleteRequested),
        mergeMap( action => 
            this.settingsService.deleteProperty(action.id).pipe(
                switchMap(res => [
                    PropertyActions.PropertyDeleteSucess({ id: action.id }),
                    ToastrActions.showToastrSuccess({message: this.i18n.t('core.properties.property_was_successfully_deleted', 'Property was successfully deleted.'), title: ''})
                ]),
                catchError((error: HttpErrorResponse) => of(
                    PropertyActions.PropertyDeleteFailed({message: error.error}),
                    ToastrActions.showToastrError({message: error.error , title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
                ))
            )
        )
    ));

    deletePropertyFailed = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.PropertyDeleteFailed),
        mergeMap(action => of(ToastrActions.showToastrError({
            message: this.i18n.t('core.properties.error_occured_while_deleting_property', 'Error occured while deleting property.') , 
            title: this.i18n.t('common.something_went_wrong', 'Something went wrong')
        })))
    ));

    saveFacebookConnect = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.FacebookConnectRequest),
        mergeMap(action => 
            this.settingsService.retrieveFacebookPages(action.token, action.user_id, action.property_id).pipe(
                switchMap(res => {
                    const modalRef = this.modalService.open(FacebookPageSelectorComponent, { centered: true });
                    modalRef.componentInstance.pages = res.model;
                    modalRef.componentInstance.property_id = action.property_id;
                    return [PropertyActions.FacebookConnectSuccess()]
                } ),
                catchError((err: HttpErrorResponse) => of(
                    PropertyActions.FacebookConnectFailed({message: err.error.detail}),
                    ToastrActions.showToastrError({message: err.error.detail, title: this.i18n.t('core.properties.error_retrieving_pages_list', 'Error Retrieving Pages List')})
                ))
            )
        )
    ));

    subscriveFacebookPages = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.FacebookSubscribeRequest),
        mergeMap(action => 
            this.settingsService.subscribeFacebookPages(action.pages, action.property_id).pipe(
                switchMap(res => {
                    const property_update: Update<Property> = {
                        id: action.property_id,
                        changes: {
                            facebook_pages: res.model
                        }
                    };
                    return [
                        PropertyActions.FacebookSubscribeSuccess({property: property_update }),
                        ToastrActions.showToastrSuccess({message: this.i18n.t('core.properties.facebook_page_was_connected', 'Facebook page was connected'), title: ''})
                    ];
                }),
                catchError((err: HttpErrorResponse) => of(
                    PropertyActions.FacebookConnectFailed({message: err.error.detail}),
                    ToastrActions.showToastrError({message: err.error.detail, title: this.i18n.t('core.properties.error_subscribing_to_facebook_pages', 'Error Subscribing to Facebook Pages')})
                ))
            )
        )
    ));


    unlinkFacebookPage = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.FacebookUnlinkRequest),
        mergeMap(action => this.settingsService.unlinkFacebookPage(action.page_id).pipe(
            switchMap(res => {
                const property_update: Update<Property> = {
                    id: action.property_id,
                    changes: {
                        facebook_pages: res.model
                    }
                };
                return [
                    PropertyActions.FacebookUnlinkSuccess({property: property_update }),
                    ToastrActions.showToastrSuccess({message: this.i18n.t('core.properties.facebook_page_was_unlinked_successfully', 'Facebook page was unlinked successfully'), title: ''})
                ];
            }),
            catchError((error: HttpErrorResponse) => of(
                PropertyActions.FacebookUnlinkFailed({message: error.error }),
                ToastrActions.showToastrError({message: this.i18n.t('core.properties.facebook_page_deleted_unsubscribe_issue', 'Your Facebook page was deleted from Charla but we faced an issue unsubscribing the page with Facebook. {{detail}}', {detail: error.error.detail}), title: this.i18n.t('core.properties.problems_unlinking_your_facebook_page', 'Problems Unlinking your Facebook page')})
                ))
        ))
    ));

    saveInstagramConnect = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.InstagramConnectRequest),
        mergeMap(action => 
            this.settingsService.connectInstagram(action.code, action.property_id).pipe(
                switchMap(res => {
                    const property_update: Update<Property> = {
                        id: action.property_id,
                        changes: {
                            instagram_accounts: res.model
                        }
                    };
                    
                    return [
                        PropertyActions.InstagramConnectSuccess({property: property_update }),
                        ToastrActions.showToastrSuccess({message: this.i18n.t('core.properties.instagram_account_was_connected', 'Instagram account was connected'), title: ''})
                    ];
                }),
                catchError((error: HttpErrorResponse) => of(
                    PropertyActions.InstagramConnectFailed({message: error.status == 400 ?
                        "Instagram Account is already linked with another property in or outside your organization.": "Error Connecting Instagram Account"}),
                    ToastrActions.showToastrError({message: error.status == 400 ?
                        this.i18n.t('core.properties.instagram_account_is_already_linked_with', 'Instagram Account is already linked with another property in or outside your organization.'): this.i18n.t('core.properties.error_connecting_instagram_account', 'Error Connecting Instagram Account') , 
                        title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
                ))
            )
        )
    ));

    unlinkInstagramPage = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.InstagramUnlinkRequest),
        mergeMap(action => this.settingsService.unlinkInstagramAccount(action.account_id).pipe(
            switchMap(res => {
                const property_update: Update<Property> = {
                    id: action.property_id,
                    changes: {
                        instagram_accounts: res.model
                    }
                };
                return [
                    PropertyActions.InstagramUnlinkSuccess({property: property_update }),
                    ToastrActions.showToastrSuccess({message: this.i18n.t('core.properties.instagram_account_was_unlinked', 'Instagram Account was unlinked successfully'), title: ''})
                ];
            }),
            catchError((error: HttpErrorResponse) => of(
                PropertyActions.InstagramUnlinkFailed({message: error.error }),
                ToastrActions.showToastrError({message: this.i18n.t('core.properties.instagram_account_deleted_unsubscribe_issue', 'Your Instagram account was deleted from Charla but we faced an issue unsubscribing with Instagram. {{detail}}', {detail: error.error.detail}), title: this.i18n.t('core.properties.problems_unlinking_your_instagram', 'Problems Unlinking your Instagram account')})
                ))
        ))
    ));

    saveWhatsappConnect = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.WhatsappConnectRequest),
        mergeMap(action => 
            this.settingsService.connectWhatsappNumber(action.code, action.property_id, action.waba_id, action.phone_number_id).pipe(
                switchMap(res => {
                    const property_update: Update<Property> = {
                        id: action.property_id,
                        changes: {
                            whatsapp_numbers: res.model
                        }
                    };
                    
                    return [
                        PropertyActions.WhatsappConnectSuccess({property: property_update }),
                        ToastrActions.showToastrSuccess({message: this.i18n.t('core.properties.your_whatsapp_number_is_now_linked', 'Your WhatsApp number is now linked to your property'), title: ''})
                    ];
                }),
                catchError((error: HttpErrorResponse) => of(
                    PropertyActions.WhatsappConnectFailed({message: error.error}),
                    ToastrActions.showToastrError({message: error.error.detail ?? this.i18n.t('core.properties.we_weren_t_able_to_register', 'We weren\'t able to register the number with WhatsApp Api. Please contact supprt.') , 
                        title: this.i18n.t('core.properties.problems_linking_to_your_whatsapp_number', 'Problems Linking to your WhatsApp number')})
                ))
            )
        )
    ));

    unlinkWhatsappNumber = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.WhatsappUnlinkRequest),
        mergeMap(action => this.settingsService.unlinkWhatsappNumber(action.number_id).pipe(
            switchMap(res => {
                const property_update: Update<Property> = {
                    id: action.property_id,
                    changes: {
                        whatsapp_numbers: res.model
                    }
                };
                return [
                    PropertyActions.WhatsappUnlinkSuccess({property: property_update }),
                    ToastrActions.showToastrSuccess({message: this.i18n.t('core.properties.whatsapp_phone_number_was_unlinked', 'WhatsApp phone number was unlinked successfully'), title: ''})
                ];
            }),
            catchError((error: HttpErrorResponse) => of(
                PropertyActions.WhatsappUnlinkFailed({message: error.error }),
                ToastrActions.showToastrError({message: this.i18n.t('core.properties.whatsapp_number_deleted_unsubscribe_issue', 'Number was deleted from Charla but we faced an issue unsubscribing with WhatsApp. {{detail}}', {detail: error.error.detail}), title: this.i18n.t('core.properties.problems_unlinking_your_whatsapp_number', 'Problems Unlinking your WhatsApp number')})
                ))
        ))
    ));

    saveTelegramConnect = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.TelegramConnectRequest),
        mergeMap(action => 
            this.settingsService.connectTelegram(action.bot_token, action.property_id).pipe(
                withLatestFrom(this.store.pipe(select(selectPropertyById({property_id: action.property_id})))),
                switchMap(([res, selected_property]) => {
                    const property_update: Update<Property> = {
                        id: action.property_id,
                        changes: {
                            telegram_bots: [...(selected_property.telegram_bots ?? []), res]
                        }
                    };
                    
                    return [
                        PropertyActions.TelegramConnectSuccess({property: property_update }),
                        ToastrActions.showToastrSuccess({message: this.i18n.t('core.properties.telegram_bot_was_connected_successfully', 'Telegram bot was connected successfully'), title: ''})
                    ];
                }),
                catchError((error: HttpErrorResponse) => of(
                     ToastrActions.showToastrError({message: error.error?.detail ?? this.i18n.t('core.properties.error_connecting_telegram_bot_please', 'Error connecting Telegram bot. Please check your bot token.') , 
                        title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
                ))
            )
        )
    ));

    unlinkTelegramBot = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.TelegramUnlinkRequest),
        mergeMap(action => this.settingsService.unlinkTelegramBot(action.bot_id).pipe(
            withLatestFrom(this.store.pipe(select(selectPropertyById({property_id: action.property_id})))),
            switchMap(([res, selected_property]) => {
                const property_update: Update<Property> = {
                    id: action.property_id,
                    changes: {
                        telegram_bots: (selected_property.telegram_bots ?? []).filter(tb => tb.id !== action.bot_id)
                    }
                };
                return [
                    PropertyActions.TelegramUnlinkSuccess({property: property_update }),
                    ToastrActions.showToastrSuccess({message: this.i18n.t('core.properties.telegram_bot_was_unlinked_successfully', 'Telegram bot was unlinked successfully'), title: ''})
                ];
            }),
            catchError((error: HttpErrorResponse) => of(
                PropertyActions.TelegramUnlinkFailed({message: error.error }),
                ToastrActions.showToastrError({message: this.i18n.t('core.properties.we_encountered_an_issue_unlinking_the', 'We encountered an issue unlinking the bot. Please try again.') , title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
                ))
        ))
    ));

    documentDeleteRequest = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.DocumentDeleteRequested),
        mergeMap(action => this.settingsService.deleteDocument(action.id).pipe(
            switchMap(res => [
                PropertyActions.DocumentDeleteSuccess({id: action.id, property_id: action.property_id}),
                ToastrActions.showToastrSuccess({message: this.i18n.t('core.properties.document_was_successfully_deleted', 'Document was successfully deleted.') , title: ''})
            ]),
            catchError((er: HttpErrorResponse) => of(
                PropertyActions.DocumentDeleteFailed({message: 'Problems while deleting the document' }),
                ToastrActions.showToastrError({message: this.i18n.t('core.properties.problems_while_deleting_the_document_the', 'Problems while deleting the document. The error has been reported.') , title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
            ))
        ))
    ));

    documentUploadRequest = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.DocumentUploadRequested),
        mergeMap(action => this.settingsService.uploadDocument(action.file, action.property_id).pipe(
            mergeMap(res => {
                
                if (res.type == HttpEventType.UploadProgress) {
                    const prog = Math.round(100 * (res.loaded / res.total));
                    return of(PropertyActions.SetUploadProgress({progress: prog}));
                } else if (res.type == HttpEventType.Response){
                    return of(
                        PropertyActions.DocumentUploadSuccess({doc: res.body.model}),
                        ToastrActions.showToastrSuccess({message: this.i18n.t('core.properties.document_was_successfully_uploaded', 'Document was successfully uploaded.') , title: ''})
                    );
                } else {
                    return of(PropertyActions.SetUploadProgress({progress: 0}));
                }
            }),
            catchError((er: Error | HttpErrorResponse) => of(
                PropertyActions.DocumentUploadFailed({message: 'Problems while uploading the document' }),
                ToastrActions.showToastrError({message: (er instanceof  HttpErrorResponse) ? this.i18n.t('core.properties.problems_while_uploading_the_document', 'Problems while uploading the document. The error has been reported.') : er.message, title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
            ))
        ))
    ));

    retrainWebpageRequest = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.RetrainWebpageRequest),
        mergeMap(action => this.settingsService.retrainWebpage(action.id).pipe(
            switchMap(res => [
                PropertyActions.SetWebpagesTrainingStatus({ webpages: [{ external_id: action.external_id, status: TrainingStatus.InProgress }], property_id: action.property_id }),
                ToastrActions.showToastrSuccess({message: this.i18n.t('core.properties.webpage_retraining_has_been_initiated', 'Webpage retraining has been initiated.') , title: ''})
            ]),
            catchError((er: HttpErrorResponse) => of(
                ToastrActions.showToastrError({message: this.i18n.t('core.properties.problems_while_retraining_the_webpage', 'Problems while retraining the webpage. The error has been reported.') , title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
            ))
        ))
    ));

    smtpTest = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.SMTPTestRequest),
        mergeMap(action => this.settingsService.smtpTest(action.host, action.port, action.username, action.password, action.from).pipe(
            switchMap(res => [
                ToastrActions.showToastrSuccess({message: this.i18n.t('core.properties.charla_successfully_connected_through', 'Charla successfully connected through SMTP. Please check your email for the test message.') , title: this.i18n.t('core.properties.email_sent', 'Email Sent')}),
                PropertyActions.SMTPTestSuccess(),
            ]),
            catchError((er: HttpErrorResponse) => of(
                ToastrActions.showToastrError({message: this.i18n.t('core.properties.problems_while_connecting_through_smtp', 'Problems while connecting through SMTP. Please make you are connecting through the TLS ports, and your username and password are correct.') , title: this.i18n.t('core.properties.smtp_connection_issue', 'SMTP Connection Issue')}),
                PropertyActions.SMTPTestFailed({message: er.message})
            ))
        ))
    ));

    mailboxGoogleConnect = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.MailboxGoogleConnectRequest),
        mergeMap(action => 
            this.settingsService.connectMailboxGoogle(action.code, action.property_id).pipe(
                switchMap(res => {
                    const property_update: Update<Property> = {
                        id: action.property_id,
                        changes: {
                            connected_mailbox: res.model.email,
                            sending_mode: 2
                        }
                    };
                    
                    return [
                        PropertyActions.MailboxGoogleConnectSuccess({property: property_update }),
                        ToastrActions.showToastrSuccess({message: this.i18n.t('core.properties.mailbox_was_connected', 'Mailbox was connected'), title: ''})
                    ];
                }),
                catchError((error: HttpErrorResponse) => of(
                    ToastrActions.showToastrError({message: this.i18n.t('core.properties.we_were_not_able_to_connect', 'We were not able to connect to your Google mailbox. The error has been reported.'),
                        title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
                ))
            )
        )
    ));

    unlinkMailbox = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.MailboxUnlinkRequest),
        mergeMap(action => this.settingsService.unlinkMailbox(action.property_id).pipe(
            switchMap(res => {
                const property_update: Update<Property> = {
                    id: action.property_id,
                    changes: {
                        connected_mailbox: null
                    }
                };
                return [
                    PropertyActions.MailboxUnlinkSuccess({property: property_update }),
                    ToastrActions.showToastrSuccess({message: this.i18n.t('core.properties.mailbox_was_unlinked_successfully', 'Mailbox was unlinked successfully'), title: ''})
                ];
            }),
            catchError((error: HttpErrorResponse) => of(
                PropertyActions.MailboxUnlinkFailed({message: "We encountered an issue unlinking the mailbox. Please try again." }),
                ToastrActions.showToastrError({message: this.i18n.t('core.properties.we_encountered_an_issue_unlinking_the_2', 'We encountered an issue unlinking the mailbox. Please try again.') , title: this.i18n.t('common.something_went_wrong', 'Something went wrong')})
                ))
        ))
    ));

    emailTemplatesRequest = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.EmailTemplateRequest),
        withLatestFrom(this.store.select(selectCurrentPropertyId)),
        mergeMap(([action, property_id]) => 
            this.settingsService.getEmailTemplates(property_id).pipe(
                switchMap(res => [
                    PropertyActions.EmailTemplateSuccess({emailTemplates: res.model, property_id})
                ]),
                catchError((er: HttpErrorResponse) => of(
                    ToastrActions.showToastrError({message: this.i18n.t('core.properties.problems_while_loading_email_templates', 'Problems while loading email templates. Please try again later.') , title: this.i18n.t('core.properties.email_templates_issue', 'Email Templates Issue')}),
                    PropertyActions.EmailTemplateFailed({message: er.message})
                ))
            )
        )
    ));

    emailTemplateSave = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.EmailTemplateSaveRequest),
        withLatestFrom(this.store.select(selectCurrentPropertyId)),
        mergeMap(([action, property_id]) => 
            this.settingsService.saveEmailTemplate({ ...action.emailTemplate, property_id }).pipe(
                switchMap(res => [
                    PropertyActions.EmailTemplateSaveSuccess({ emailTemplate: res.model }),
                    ToastrActions.showToastrSuccess({message: this.i18n.t('core.properties.email_template_was_successfully_saved', 'Email template was successfully saved.') , title: ''})
                ]),
                catchError((er: HttpErrorResponse) => of(
                    ToastrActions.showToastrError({message: this.i18n.t('core.properties.problems_while_saving_email_template', 'Problems while saving email template. Please try again.') , title: this.i18n.t('core.properties.email_template_issue', 'Email Template Issue')}),
                    PropertyActions.EmailTemplateSaveFailed({message: er.message})
                ))
            )
        )
    ));


    emailTemplateReset = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.EmailTemplateResetRequest),
        withLatestFrom(this.store.select(selectCurrentPropertyId)),
        mergeMap(([action, property_id]) => 
            this.settingsService.resetEmailTemplate({...action.emailTemplate, property_id}).pipe(
                switchMap(res => [
                    PropertyActions.EmailTemplateResetSuccess({emailTemplate: res.model}),
                    ToastrActions.showToastrSuccess({message: this.i18n.t('core.properties.email_template_was_successfully_reset', 'Email template was successfully reset.') , title: ''})

                ]),
                catchError((er: HttpErrorResponse) => of(
                    ToastrActions.showToastrError({message: this.i18n.t('core.properties.problems_while_resetting_email_template', 'Problems while resetting email template. Please try again later.') , title: this.i18n.t('core.properties.email_template_issue', 'Email Template Issue')}),
                    PropertyActions.EmailTemplateResetFailed({message: er.message})
                ))
            )
        )
    ));

    scrapedWebpageRequest = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.ScrapedWebpageContentRequest),
        mergeMap(action => this.settingsService.getScrapedWebpage(action.page_id).pipe(
            switchMap(res => [
                PropertyActions.ScrapedWebpageContentSuccess({content: res.model.content})
            ]),
            catchError((er: HttpErrorResponse) => of(
                ToastrActions.showToastrError({message: this.i18n.t('core.properties.problems_while_retrieving_scraped', 'Problems while retrieving scraped webpage content. Please try again later.') , title: this.i18n.t('core.properties.scraped_webpage_issue', 'Scraped Webpage Issue')}),
                PropertyActions.ScrapedWebpageContentFailed({message: er.message})
            ))
        ))
    ));

    showScrapedPagePreview = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.ScrapedWebpageContentSuccess),
        tap(s => {
            const modalRef = this.modalService.open(CharlaScrapedPagePreviewComponent, { centered: true, windowClass: 'charla-dialog-xl' });
            modalRef.componentInstance.scraped_page = s.content;
        })
    ), { dispatch: false });

    saveFlow = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.FlowSaveRequest),
        mergeMap(action => 
            this.settingsService.saveFlow(action.flow).pipe(
                switchMap(res => [
                    PropertyActions.FlowSaveSuccess({flow: res}),
                    ToastrActions.showToastrSuccess({message: this.i18n.t('core.properties.flow_was_successfully_saved', 'Flow was successfully saved.') , title: ''})
                ] ),
                catchError((err: HttpErrorResponse) => of(
                    PropertyActions.FlowSaveFailed({message: err.error.detail}),
                    ToastrActions.showToastrError({message: err.error.detail, title: this.i18n.t('core.properties.error_saving_flow', 'Error Saving Flow')})
                ))
            )
        )
    ));

    deleteFlow = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.FlowDeleteRequest),
        mergeMap(action => 
            this.settingsService.deleteFlow(action.flow_id).pipe(
                switchMap(res => [
                    PropertyActions.FlowDeleteSuccess({flow_id: action.flow_id, property_id: action.property_id}),
                    ToastrActions.showToastrSuccess({message: this.i18n.t('core.properties.flow_was_successfully_deleted', 'Flow was successfully deleted.') , title: ''})
                ]),
                catchError((err: HttpErrorResponse) => of(
                    PropertyActions.FlowDeleteFailed({message: err.error?.detail ?? 'Error deleting flow'}),
                    ToastrActions.showToastrError({message: err.error?.detail ?? this.i18n.t('core.properties.error_deleting_flow', 'Error deleting flow'), title: this.i18n.t('core.properties.error_deleting_flow_2', 'Error Deleting Flow')})
                ))
            )
        )
    ));

    toggleFlow = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.FlowToggleRequest),
        mergeMap(action => 
            this.settingsService.toggleFlow(action.flow_id).pipe(
                switchMap((res: any) => [
                    PropertyActions.FlowToggleSuccess({flow_id: res.id, is_enabled: res.is_enabled, property_id: res.property_id}),
                    ToastrActions.showToastrSuccess({message: this.i18n.t('core.properties.flow_status_updated_successfully', 'Flow status updated successfully.') , title: ''})
                ]),
                catchError((err: HttpErrorResponse) => of(
                    PropertyActions.FlowToggleFailed({message: err.error?.detail ?? 'Error toggling flow'}),
                    ToastrActions.showToastrError({message: err.error?.detail ?? this.i18n.t('core.properties.error_toggling_flow', 'Error toggling flow'), title: this.i18n.t('core.properties.error_updating_flow', 'Error Updating Flow')})
                ))
            )
        )
    ));

    getProducts = createEffect(() => this.actions$.pipe(
        ofType(PropertyActions.ProductsRequest, PropertyActions.ProductTrainingStatusUpdate),
        mergeMap(action => {
            const page = 'page' in action ? action.page ?? 1 : 1;
            const limit = 'limit' in action ? action.limit ?? 20 : 20;
            return this.settingsService.getProducts(action.property_id, page, limit).pipe(
                switchMap(res => [
                    PropertyActions.ProductsRequestSuccess({
                        products: res.model,
                        property_id: action.property_id,
                        page: res.pageNumber,
                        limit: res.pageSize,
                        total: res.itemsCount
                    })
                ]),
                catchError((err: HttpErrorResponse) => of(
                    PropertyActions.ProductsRequestFailed({message: err.error?.message ?? 'Error loading products'}),
                    ToastrActions.showToastrError({message: err.error?.message ?? this.i18n.t('core.properties.error_loading_products', 'Error loading products'), title: this.i18n.t('core.properties.error', 'Error')})
                ))
            )
        })
    ));

    constructor(
        private settingsService: SettingsService,
        private actions$: Actions,
        private store: Store<AppState>,
        private modalService: NgbModal
    ) { }

}